import { Bell } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Separator,
} from '@mlm/ui';
import {
  getUnreadCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../lib/api';

/**
 * JM-EX9 bell. Polls the unread count every 60s + on focus so a member sees
 * fresh earnings drop into the icon without a page refresh. The dropdown
 * lists the 10 most recent; "Mark all read" zeros the badge. Clicking the
 * "See all" link routes to /notifications (the full paginated page).
 *
 * No Socket.IO live push yet — the worker doesn't share an IO instance with
 * the API in the current deployment. The 60s poll covers the realistic gap.
 */
export function NotificationsBell({ compact = false }: { compact?: boolean } = {}) {
  const qc = useQueryClient();
  const unreadQ = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: getUnreadCount,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const recentQ = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => listNotifications({ limit: 10 }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const markAll = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
  const markOne = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const unread = unreadQ.data?.count ?? 0;
  const items = recentQ.data?.items ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        className={`relative flex items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          compact ? 'h-7 w-7' : 'h-9 w-9'
        }`}
      >
        <Bell className={compact ? 'h-[18px] w-[18px]' : 'h-4 w-4'} />
        {unread > 0 && (
          <span className="absolute right-1 top-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium tabular-nums leading-none text-primary-foreground">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-2 pt-1">
          <DropdownMenuLabel className="px-0">Notifications</DropdownMenuLabel>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              Mark all read
            </Button>
          )}
        </div>
        <Separator />
        {items.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            No notifications yet.
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto">
            {items.map((n) => (
              <li
                key={n.id}
                className={`cursor-pointer border-b px-3 py-2 last:border-b-0 hover:bg-muted/50 ${
                  n.readAt ? 'opacity-70' : ''
                }`}
                onClick={() => {
                  if (!n.readAt) markOne.mutate(n.id);
                }}
              >
                <div className="flex items-start gap-2">
                  {!n.readAt && (
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{n.title}</p>
                    {n.body && (
                      <p className="line-clamp-2 text-xs text-muted-foreground">{n.body}</p>
                    )}
                    <p className="mt-0.5 text-[10px] text-muted-foreground">
                      {formatRelative(n.createdAt)}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
        <Separator />
        <div className="px-2 py-1">
          <Link
            to="/notifications"
            className="block rounded-md px-2 py-1 text-center text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            See all notifications
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
